import { useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ChevronUp,
  ChevronDown,
  Check,
  Table2,
  Tags,
  ChefHat,
  Clock,
  Undo2,
  RefreshCw,
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  UsersRound,
  User,
} from "lucide-react";

/**
 * ΣΕΡΒΙΡΙΣΜΑ (runner / food-running board) — Ιούν 2026.
 * Εμφανίζει ΟΛΑ τα είδη όλων των ανοιχτών τραπεζιών. `compact` = mobile layout
 * (μικρότερα πλήκτρα/γράμματα, σαν το view «Παραγγελίες»).
 */
const GROUPINGS = [
  { id: "table", label: "Ανά Τραπέζι", icon: Table2 },
  { id: "category", label: "Ανά Κατηγορία", icon: Tags },
  { id: "printer", label: "Παρασκευαστήριο", icon: ChefHat },
];

function waitLabel(since, nowMs) {
  if (!since) return "";
  const mins = Math.max(0, Math.floor((nowMs - new Date(since).getTime()) / 60000));
  if (mins < 60) return `${mins}′`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}ω ${String(m).padStart(2, "0")}′`;
}

function waitColor(since, nowMs) {
  const mins = Math.max(0, Math.floor((nowMs - new Date(since).getTime()) / 60000));
  if (mins >= 20) return "bg-red-100 text-red-700 border-red-300";
  if (mins >= 10) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-emerald-100 text-emerald-700 border-emerald-300";
}

export default function ServingScreen({ onClose, compact = false, waiterName = "", waiterId = "" }) {
  const [items, setItems] = useState([]);
  const [grouping, setGrouping] = useState("table");
  const [showServed, setShowServed] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [sortDir, setSortDir] = useState("asc"); // asc = παλαιότερο πρώτα
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [qtySel, setQtySel] = useState({});
  const pollRef = useRef(null);

  // Μεγέθη ανά περιβάλλον (mobile vs POS).
  const S = compact
    ? {
        headerPad: "px-3 py-2 gap-2",
        title: "text-lg",
        smallBtn: "h-9 px-2 text-xs",
        smallIcon: 15,
        groupBtn: "h-10 px-2.5 text-xs gap-1.5",
        groupIcon: 16,
        summaryPad: "px-3 py-1.5 gap-1.5",
        body: "p-2 space-y-3",
        grpHeader: "text-sm px-3 py-1",
        grpWrap: "space-y-1.5 border-2 border-slate-800 rounded-xl p-1.5",
        grpWrapPlain: "space-y-1.5",
        rowPad: "gap-2 px-2 py-1.5",
        timeBadge: "text-[11px] px-1.5 py-1 gap-0.5",
        timeIcon: 12,
        tableBox: "min-w-[52px] max-w-[80px] h-9 px-1.5",
        tableTxt: "text-xs",
        desc: "text-sm",
        extra: "text-[9px] px-1 py-0.5",
        ctx: "text-[9px]",
        stepBtn: "h-9 w-8",
        stepIcon: 20,
        qtyBox: "w-9 h-9",
        qtyNum: "text-base",
        okBtn: "h-9 w-12 text-sm",
        servedBadge: "text-sm px-2 py-1",
        restoreBtn: "h-9 px-2 text-xs",
      }
    : {
        headerPad: "px-5 py-3 gap-3",
        title: "text-2xl",
        smallBtn: "h-12 px-4 text-base",
        smallIcon: 18,
        groupBtn: "h-14 px-5 text-base gap-2",
        groupIcon: 20,
        summaryPad: "px-5 py-2 gap-2",
        body: "p-4 space-y-5",
        grpHeader: "text-lg px-4 py-1.5",
        grpWrap: "space-y-2 border-[3px] border-slate-800 rounded-2xl p-3 bg-slate-100/60",
        grpWrapPlain: "space-y-2",
        rowPad: "gap-3 p-3",
        timeBadge: "text-base px-3 py-2 gap-1",
        timeIcon: 16,
        tableBox: "min-w-[90px] max-w-[150px] h-14 px-3",
        tableTxt: "text-lg",
        desc: "text-xl",
        extra: "text-xs px-1.5 py-0.5",
        ctx: "text-xs",
        stepBtn: "h-16 w-14",
        stepIcon: 30,
        qtyBox: "w-16 h-16",
        qtyNum: "text-3xl",
        okBtn: "h-16 w-24 text-2xl",
        servedBadge: "text-lg px-3 py-1.5",
        restoreBtn: "h-14 px-4 text-base",
      };

  const load = async () => {
    try {
      const r = await http.get("/serving/board");
      setItems(r.data.items || []);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    const clk = setInterval(() => setNowMs(Date.now()), 15000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(clk);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSel = (it) => {
    const v = qtySel[it.key];
    if (v == null) return Math.max(1, Math.round(it.remaining));
    return Math.min(Math.max(1, v), Math.max(1, Math.round(it.remaining)));
  };

  const bumpSel = (it, delta) => {
    const cur = getSel(it);
    const next = Math.min(Math.max(1, cur + delta), Math.max(1, Math.round(it.remaining)));
    setQtySel((s) => ({ ...s, [it.key]: next }));
  };

  const serve = async (it, qty) => {
    setBusyKey(it.key);
    try {
      await http.post("/serving/serve", {
        table_id: it.table_id,
        location: it.location,
        batch_no: it.batch_no,
        item_idx: it.item_idx,
        product_id: it.product_id,
        qty,
      });
      setQtySel((s) => {
        const n = { ...s };
        delete n[it.key];
        return n;
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα σερβιρίσματος");
      await load();
    } finally {
      setBusyKey(null);
    }
  };

  const scoped = useMemo(
    () =>
      mineOnly && waiterId
        ? items.filter((it) => it.user_id && it.user_id === waiterId)
        : items,
    [items, mineOnly, waiterId],
  );

  const visible = useMemo(
    () =>
      showServed
        ? scoped.filter((it) => (it.served_qty || 0) > 0)
        : scoped.filter((it) => (it.remaining || 0) > 0),
    [scoped, showServed],
  );

  const groups = useMemo(() => {
    const map = new Map();
    const push = (key, label, it) => {
      if (!map.has(key)) map.set(key, { key, label, items: [], since: it.since });
      const g = map.get(key);
      g.items.push(it);
      if ((it.since || "") < (g.since || "")) g.since = it.since;
    };
    for (const it of visible) {
      if (grouping === "table") push(it.table_id, it.table_name, it);
      else if (grouping === "category") push(it.category_id || "none", it.category_name, it);
      else {
        const names = it.printer_names && it.printer_names.length ? it.printer_names : ["—"];
        for (const pn of names) push(`p:${pn}`, pn === "—" ? "Χωρίς εκτυπωτή" : pn, it);
      }
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => dir * (a.since || "").localeCompare(b.since || "");
    const arr = Array.from(map.values());
    arr.forEach((g) => g.items.sort(cmp));
    arr.sort(cmp);
    return arr;
  }, [visible, grouping, sortDir]);

  const sectionSummary = useMemo(() => {
    const map = new Map();
    for (const it of scoped) {
      const rem = it.remaining || 0;
      if (rem <= 0) continue;
      const name = it.section_name || "Χωρίς τμήμα";
      map.set(name, (map.get(name) || 0) + rem);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count: Math.round(count) }))
      .sort((a, b) => b.count - a.count);
  }, [scoped]);

  const totalPending = scoped.reduce((n, it) => n + (it.remaining || 0), 0);

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="serving-screen">
      {/* Header */}
      {compact ? (
        <div className="border-b-2 border-slate-200 bg-white shadow-sm px-3 py-2 space-y-2">
          {/* Row 1: Πίσω + τίτλος + όνομα σερβιτόρου */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-9 px-2 text-xs border-2 font-bold"
              onClick={onClose}
              data-testid="serving-back-btn"
            >
              <ArrowLeft size={15} className="mr-1" /> Πίσω
            </Button>
            <h2 className="font-heading font-extrabold tracking-tight text-slate-800 text-lg">
              ΣΕΡΒΙΡΙΣΜΑ
            </h2>
            {waiterName && (
              <span className="ml-auto flex items-center gap-1 text-xs font-mono font-bold text-slate-600" data-testid="serving-waiter-name">
                <UsersRound size={13} className="text-emerald-600" /> {waiterName}
              </span>
            )}
          </div>
          {/* Row 2: «σε αναμονή» + sort + refresh + σερβιρισμένα */}
          <div className="flex items-center gap-1.5">
            {waiterId && (
              <Button
                variant={mineOnly ? "default" : "outline"}
                className={`h-9 px-2 text-xs border-2 font-bold ${mineOnly ? "bg-indigo-600 text-white border-indigo-600" : ""}`}
                onClick={() => setMineOnly((v) => !v)}
                data-testid="serving-toggle-mine"
              >
                {mineOnly ? <User size={15} className="mr-1" /> : <UsersRound size={15} className="mr-1" />}
                {mineOnly ? "Δικά μου" : "Όλες"}
              </Button>
            )}
            {!showServed && (
              <span
                className="rounded-full bg-slate-800 text-white font-bold text-[11px] px-2 py-1"
                data-testid="serving-pending-count"
              >
                {Math.round(totalPending)} σε αναμονή
              </span>
            )}
            {!showServed && (
              <Button
                variant="outline"
                className="h-9 px-2 text-xs border-2 font-bold"
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                data-testid="serving-sort-toggle"
              >
                {sortDir === "asc" ? (
                  <><ArrowDownWideNarrow size={15} className="mr-1" /> Παλαιότερο</>
                ) : (
                  <><ArrowUpWideNarrow size={15} className="mr-1" /> Νεότερο</>
                )}
              </Button>
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <Button
                variant="outline"
                className="h-9 px-2 border-2"
                onClick={load}
                data-testid="serving-refresh-btn"
              >
                <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              </Button>
              <Button
                variant={showServed ? "default" : "outline"}
                className={`h-9 px-2 text-xs border-2 font-bold ${showServed ? "bg-slate-800 text-white" : ""}`}
                onClick={() => setShowServed((v) => !v)}
                data-testid="serving-toggle-served"
              >
                <Check size={15} className="mr-1" />
                {showServed ? "Αναμονή" : "Σερβιρισμένα"}
              </Button>
            </div>
          </div>
        </div>
      ) : (
      <div className={`border-b-2 border-slate-200 bg-white flex flex-wrap items-center shadow-sm ${S.headerPad}`}>
        <Button
          variant="outline"
          className={`border-2 font-bold ${S.smallBtn}`}
          onClick={onClose}
          data-testid="serving-back-btn"
        >
          <ArrowLeft size={S.smallIcon} className="mr-1.5" /> Πίσω
        </Button>
        <h2 className={`font-heading font-extrabold tracking-tight text-slate-800 ${S.title}`}>
          ΣΕΡΒΙΡΙΣΜΑ
        </h2>
        {!showServed && (
          <span
            className="rounded-full bg-slate-800 text-white font-bold text-sm px-3 py-1"
            data-testid="serving-pending-count"
          >
            {Math.round(totalPending)} σε αναμονή
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {waiterId && (
            <Button
              variant={mineOnly ? "default" : "outline"}
              className={`border-2 font-bold ${S.smallBtn} ${mineOnly ? "bg-indigo-600 text-white border-indigo-600" : ""}`}
              onClick={() => setMineOnly((v) => !v)}
              data-testid="serving-toggle-mine"
            >
              {mineOnly ? <User size={S.smallIcon} className="mr-1" /> : <UsersRound size={S.smallIcon} className="mr-1" />}
              {mineOnly ? "Δικά μου" : "Όλες"}
            </Button>
          )}
          {!showServed && (
            <Button
              variant="outline"
              className={`border-2 font-bold ${S.smallBtn}`}
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              data-testid="serving-sort-toggle"
            >
              {sortDir === "asc" ? (
                <><ArrowDownWideNarrow size={S.smallIcon} className="mr-1" /> Παλαιότερο</>
              ) : (
                <><ArrowUpWideNarrow size={S.smallIcon} className="mr-1" /> Νεότερο</>
              )}
            </Button>
          )}
          <Button
            variant="outline"
            className="border-2 h-12 px-3"
            onClick={load}
            data-testid="serving-refresh-btn"
            title="Ανανέωση"
          >
            <RefreshCw size={S.smallIcon} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button
            variant={showServed ? "default" : "outline"}
            className={`border-2 font-bold ${S.smallBtn} ${showServed ? "bg-slate-800 text-white" : ""}`}
            onClick={() => setShowServed((v) => !v)}
            data-testid="serving-toggle-served"
          >
            <Check size={S.smallIcon} className="mr-1" />
            {showServed ? "Αναμονή" : "Σερβιρισμένα"}
          </Button>
        </div>
      </div>
      )}

      {/* Grouping toolbar */}
      {!showServed && (
        <div className={`bg-white border-b border-slate-200 flex gap-1.5 ${compact ? "flex-nowrap px-2 py-2" : "flex-wrap px-5 py-3 gap-2"}`}>
          {GROUPINGS.map((g) => {
            const Icon = g.icon;
            const active = grouping === g.id;
            return (
              <button
                key={g.id}
                onClick={() => setGrouping(g.id)}
                data-testid={`serving-group-${g.id}`}
                className={`flex items-center justify-center rounded-xl border-2 font-extrabold transition-all active:scale-95 ${
                  compact ? "flex-1 h-10 px-1 text-[11px] leading-tight gap-1" : S.groupBtn
                } ${
                  active
                    ? "bg-indigo-600 border-indigo-600 text-white shadow-md"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                {!compact && <Icon size={S.groupIcon} />}
                {g.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Σύνοψη ανά τμήμα */}
      {!showServed && sectionSummary.length > 0 && (
        <div
          className={`bg-slate-100 border-b border-slate-200 flex flex-wrap items-center ${S.summaryPad}`}
          data-testid="serving-section-summary"
        >
          <span className={`font-bold uppercase tracking-wide text-slate-500 mr-1 ${compact ? "text-[10px]" : "text-xs"}`}>
            Ανά τμήμα:
          </span>
          {sectionSummary.map((s) => (
            <span
              key={s.name}
              className={`inline-flex items-center gap-1.5 bg-white border-2 border-slate-300 rounded-lg shadow-sm ${compact ? "px-2 py-0.5" : "px-3 py-1"}`}
              data-testid={`serving-section-chip-${s.name}`}
            >
              <span className={`font-bold text-slate-700 ${compact ? "text-xs" : "text-sm"}`}>{s.name}</span>
              <span className={`font-black text-white bg-indigo-600 rounded-md text-center ${compact ? "text-xs px-1.5 min-w-[20px]" : "text-sm px-2 py-0.5 min-w-[26px]"}`}>
                {s.count}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Body */}
      <div className={`flex-1 overflow-y-auto scroll-thin ${S.body}`}>
        {groups.length === 0 && (
          <div className={`text-center text-slate-400 mt-16 font-semibold ${compact ? "text-base" : "text-lg"}`} data-testid="serving-empty">
            {showServed ? "Δεν έχει σερβιριστεί κάτι ακόμα." : "Κανένα είδος σε αναμονή."}
          </div>
        )}
        {groups.map((g) => (
          <div key={g.key} data-testid={`serving-grp-${g.key}`}>
            <div className="flex items-center gap-2 mb-1.5">
              <div className={`bg-slate-800 text-white font-extrabold rounded-lg shadow ${S.grpHeader}`}>
                {g.label}
              </div>
              <div className="h-[2px] flex-1 bg-slate-200" />
              <span className={`font-bold text-slate-500 ${compact ? "text-xs" : "text-sm"}`}>{g.items.length} είδη</span>
            </div>
            <div className={grouping === "table" ? S.grpWrap : S.grpWrapPlain}>
              {g.items.map((it) => {
                const sel = getSel(it);
                const multi = Math.round(it.remaining) > 1;
                return (
                  <div
                    key={it.key}
                    className={`flex ${compact ? "flex-nowrap" : "flex-wrap"} items-center rounded-xl border-2 border-slate-200 shadow-sm ${S.rowPad}`}
                    style={{
                      borderLeft: `${compact ? 7 : 10}px solid ${it.color || "#64748b"}`,
                      backgroundColor: `${it.color || "#64748b"}12`,
                    }}
                    data-testid={`serving-row-${it.key}`}
                  >
                    {compact ? (
                      /* Στοίβα: τραπέζι πάνω, χρόνος από κάτω */
                      <div className="shrink-0 flex flex-col items-stretch gap-0.5 w-[68px]">
                        <div
                          className="rounded-md bg-slate-800 text-white flex items-center justify-center shadow h-7 px-1"
                          data-testid={`serving-table-box-${it.key}`}
                        >
                          <span className="font-black leading-tight text-center truncate text-xs">
                            {it.table_name}
                          </span>
                        </div>
                        <span
                          className={`flex items-center justify-center gap-0.5 font-extrabold rounded-md border text-[10px] py-0.5 ${waitColor(it.since, nowMs)}`}
                          title="Χρόνος αναμονής"
                        >
                          <Clock size={11} /> {waitLabel(it.since, nowMs)}
                        </span>
                      </div>
                    ) : (
                      <>
                        {/* Χρόνος */}
                        <span
                          className={`shrink-0 flex items-center font-extrabold rounded-lg border ${S.timeBadge} ${waitColor(it.since, nowMs)}`}
                          title="Χρόνος αναμονής"
                        >
                          <Clock size={S.timeIcon} /> {waitLabel(it.since, nowMs)}
                        </span>
                        {/* Όνομα τραπεζιού */}
                        <div
                          className={`shrink-0 rounded-lg bg-slate-800 text-white flex items-center justify-center shadow ${S.tableBox}`}
                          data-testid={`serving-table-box-${it.key}`}
                        >
                          <span className={`font-black leading-tight text-center truncate ${S.tableTxt}`}>
                            {it.table_name}
                          </span>
                        </div>
                      </>
                    )}

                    {/* Περιγραφή + extras */}
                    <div className="flex-1 min-w-0">
                      <div className={`font-extrabold text-slate-800 truncate ${S.desc}`}>
                        {it.description}
                      </div>
                      {it.extras && it.extras.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`serving-extras-${it.key}`}>
                          {it.extras.map((ex, i) => (
                            <span
                              key={i}
                              className={`font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded ${S.extra}`}
                            >
                              {ex}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className={`font-semibold text-slate-400 truncate ${S.ctx}`}>
                        {grouping !== "category" ? it.category_name : ""}
                        {it.location === "open" ? " · ακαταχώρητο" : ""}
                      </div>
                    </div>

                    {showServed ? (
                      <div className={compact ? "flex items-center gap-1.5 ml-auto" : "flex items-center gap-3"}>
                        <span className={`shrink-0 font-extrabold text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-lg ${S.servedBadge}`}>
                          {Math.round(it.served_qty)}/{Math.round(it.qty)}
                        </span>
                        <Button
                          variant="outline"
                          className={`shrink-0 border-2 border-amber-400 text-amber-700 font-extrabold ${S.restoreBtn}`}
                          disabled={busyKey === it.key}
                          onClick={() => serve(it, -it.served_qty)}
                          data-testid={`serving-restore-${it.key}`}
                        >
                          <Undo2 size={S.smallIcon} className="mr-1" /> Επαναφορά
                        </Button>
                      </div>
                    ) : (
                      <div className={compact ? "flex items-center gap-1 ml-auto" : "flex items-center gap-3"}>
                        {/* Stepper — βελάκια δίπλα-δίπλα */}
                        <div className="shrink-0 flex items-center gap-1">
                          {multi && (
                            <button
                              onClick={() => bumpSel(it, -1)}
                              data-testid={`serving-qty-down-${it.key}`}
                              className={`rounded-lg bg-slate-100 border-2 border-slate-300 flex items-center justify-center active:scale-95 hover:bg-slate-200 ${S.stepBtn}`}
                            >
                              <ChevronDown size={S.stepIcon} strokeWidth={3} />
                            </button>
                          )}
                          <div
                            className={`rounded-lg bg-indigo-50 border-2 border-indigo-300 flex flex-col items-center justify-center ${S.qtyBox}`}
                            data-testid={`serving-qty-${it.key}`}
                          >
                            <span className={`font-black text-indigo-700 leading-none ${S.qtyNum}`}>{sel}</span>
                            {multi && (
                              <span className="text-[8px] font-bold text-indigo-400 leading-none mt-0.5">/{Math.round(it.remaining)}</span>
                            )}
                          </div>
                          {multi && (
                            <button
                              onClick={() => bumpSel(it, +1)}
                              data-testid={`serving-qty-up-${it.key}`}
                              className={`rounded-lg bg-slate-100 border-2 border-slate-300 flex items-center justify-center active:scale-95 hover:bg-slate-200 ${S.stepBtn}`}
                            >
                              <ChevronUp size={S.stepIcon} strokeWidth={3} />
                            </button>
                          )}
                        </div>
                        {/* OK */}
                        <Button
                          className={`shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-lg shadow-md active:scale-95 ${S.okBtn}`}
                          disabled={busyKey === it.key}
                          onClick={() => serve(it, sel)}
                          data-testid={`serving-ok-${it.key}`}
                        >
                          ΟΚ
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
